import React from 'react';
import type { ResponseCluster } from '@mentimeter/http-clients';
import type { GroupShapeMap } from '@mentimeter/slides-presentation-ui/groups/group-shapes';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { SparkleSolidIcon } from '@mentimeter/ragnar-visuals';
import { useTranslations } from 'next-intl';
import { GroupDetailViewDataHandler } from '../group-detail-view/GroupDetailViewDataHandler';
import { GroupsOverviewCards } from '../GroupsOverviewCards';
import { FadeIn } from '../motion/FadeIn';
import { AnimatedSubheading } from '../motion/AnimatedSubheading';
import { OpenEndedTitleDataHandler } from '../../ui/OpenEndedTitleDataHandler';

export interface GroupsWithVotesProps {
  groups: ResponseCluster[];
  clusteringId: string | undefined;
  groupShapeMap: GroupShapeMap;
  shouldHideResponses: boolean;
}

export const GroupsWithVotes = ({
  groups,
  clusteringId,
  groupShapeMap,
  shouldHideResponses,
}: GroupsWithVotesProps) => {
  const translate = useTranslations('presenting.legacy');

  const { groupsWithVotes, groupsWithoutVotes } = groups.reduce<{
    groupsWithVotes: ResponseCluster[];
    groupsWithoutVotes: ResponseCluster[];
  }>(
    (acc, cluster) => {
      if (cluster === undefined) {
        return acc;
      }
      if (cluster.total_upvotes) {
        acc.groupsWithVotes.push(cluster);
      } else {
        acc.groupsWithoutVotes.push(cluster);
      }

      return acc;
    },
    { groupsWithVotes: [], groupsWithoutVotes: [] },
  );

  const shouldShowUnvotedGroups = !!groupsWithoutVotes.length;

  const maxGroupVoteCount = Math.max(
    ...groups.map((group) => group?.total_upvotes ?? 0),
  );

  const numberOfGroupsWithVotes = groupsWithVotes.length;

  const subheadingText =
    numberOfGroupsWithVotes === 1
      ? translate('open.group_with_votes')
      : translate('open.groups_with_votes', {
          numberOfGroups: `${numberOfGroupsWithVotes ?? 0}`,
        });

  const hasGroupsWithVotes = groupsWithVotes.length > 0;

  return (
    <Box width="100%" height="100%" flex="1 1 auto" overflow="hidden">
      <GroupDetailViewDataHandler
        groups={groups}
        shouldShowVotes={true}
        clusteringId={clusteringId}
        groupShapeMap={groupShapeMap}
      />
      <OpenEndedTitleDataHandler>
        <AnimatedSubheading shouldShow={true}>
          <Box mr={3}>
            <SparkleSolidIcon size={3} color="textWeak" />
          </Box>
          <Text
            as="h2"
            color="textWeak"
            fontSize="200"
            mt="space1"
            lineHeight="none"
          >
            {subheadingText}
          </Text>
        </AnimatedSubheading>
      </OpenEndedTitleDataHandler>
      {!shouldHideResponses && (
        <Box flex="1 1 auto" width="100%" gap={4} overflow="auto">
          <GroupsOverviewCards
            groups={groupsWithVotes}
            maxGroupVoteCount={maxGroupVoteCount}
            shouldShowVotes
            groupShapeMap={groupShapeMap}
          />
          {shouldShowUnvotedGroups && (
            <FadeIn delay={hasGroupsWithVotes ? 900 : 0} width="100%" gap={3}>
              {hasGroupsWithVotes && (
                <Text
                  as="h2"
                  fontSize={4}
                  fontWeight="regular"
                  fontFamily="heading"
                  color="textWeaker"
                  width="100%"
                  lineHeight="heading"
                >
                  {translate('open.groups_without_votes')}
                </Text>
              )}
              <GroupsOverviewCards
                groups={groupsWithoutVotes}
                maxGroupVoteCount={maxGroupVoteCount}
                shouldShowVotes
                groupShapeMap={groupShapeMap}
              />
            </FadeIn>
          )}
        </Box>
      )}
    </Box>
  );
};
